import { type SelectOwnerState } from '@mui/base'
import { clsx } from 'clsx'
import { type RangeBoundaries } from 'instantsearch.js/es/connectors/range/connectRange'
import { type ComponentProps, type JSX, useEffect, useState } from 'react'

import { Slider } from 'base-ui'

import { useProductSearchRefinementContext } from '../ProductSearchRefinement'
import { ProductSearchRefinementClearRefinement } from '../ProductSearchRefinementClearRefinement'

import { ProductSearchRefinementPriceInput } from './ProductSearchRefinementPriceInput'

import styles from './ProductSearchRefinementPriceListboxBody.module.css'

const DELIMITER_LABEL = '—'

const FIXED_DECIMAL_POINT = 2

const normalizeRangeInputValue = (value: number): number =>
  Number.parseFloat(value.toFixed(FIXED_DECIMAL_POINT))

type Range = {
  max?: number
  min?: number
}

const PRICE_DEFAULT_VALUES = {
  max: 1e10,
  min: 0,
} satisfies Range

const getInitialValues = (
  range: Range,
  start: RangeBoundaries,
): { from: number; to: number } => {
  const { max, min } = range
  const from =
    min && Math.max(min, start[0] && Number.isFinite(start[0]) ? start[0] : min)
  const to =
    max && Math.min(max, start[1] && Number.isFinite(start[1]) ? start[1] : max)

  return {
    from: from
      ? normalizeRangeInputValue(from / 100)
      : PRICE_DEFAULT_VALUES.min,
    to: to ? normalizeRangeInputValue(to / 100) : PRICE_DEFAULT_VALUES.max,
  }
}

const getDefaultRange = (range: Range): Required<Range> => {
  const { max, min } = range

  return {
    max: max ? normalizeRangeInputValue(max / 100) : PRICE_DEFAULT_VALUES.max,
    min: min ? normalizeRangeInputValue(min / 100) : PRICE_DEFAULT_VALUES.min,
  }
}

type ProductSearchRefinementPriceListboxBodyProps<
  OptionValue extends NonNullable<unknown>,
  Multiple extends boolean,
> = ComponentProps<'div'> & {
  ownerState: SelectOwnerState<OptionValue, Multiple>
}

export const ProductSearchRefinementPriceListboxBody = <
  OptionValue extends NonNullable<unknown>,
  Multiple extends boolean,
>(
  props: ProductSearchRefinementPriceListboxBodyProps<OptionValue, Multiple>,
): JSX.Element => {
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- not used for rendering
    children,
    className,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- not used for rendering
    ownerState,
    ...restProps
  } = props

  const refinementContext = useProductSearchRefinementContext()

  if (!('refinementRange' in refinementContext)) {
    throw new Error('The refinement is not a range refinement.')
  }

  const { refinementRange, refinementRangeOptions } = refinementContext

  const { attribute } = refinementRangeOptions

  const { canRefine, range, refine, start } = refinementRange

  const { max, min } = getDefaultRange(range)

  const [inputRange, setInputRange] = useState(getInitialValues(range, start))
  const { from, to } = inputRange

  useEffect(() => {
    setInputRange(getInitialValues(range, start))
  }, [range, start])

  const applyRange = (): void => {
    const newMin = from * 100
    const newMax = to * 100
    refine([newMin, newMax])
  }

  return (
    <div
      className={clsx(
        styles['product-search-refinement-price-listbox-body'],
        className,
      )}
      {...restProps}
    >
      <Slider
        className="w-full"
        defaultValue={[min, max]}
        disabled={!canRefine}
        max={max}
        min={min}
        onChange={(event, values) => {
          if (Array.isArray(values)) {
            setInputRange({ from: values[0], to: values[1] })
          }
        }}
        onChangeCommitted={() => {
          applyRange()
        }}
        step={0.01}
        value={[from, to]}
      />

      <div className="flex items-center gap-4 self-stretch desktop:gap-1">
        <ProductSearchRefinementPriceInput
          className="flex-1 text-dark-primary-strong"
          disabled={!canRefine}
          onChange={(event) => {
            const value = Number(event.target.value)
            setInputRange({ from: value, to })
          }}
          onKeyUp={() => {
            applyRange()
          }}
          value={from}
        />
        {DELIMITER_LABEL}
        <ProductSearchRefinementPriceInput
          className="flex-1 text-dark-primary-strong"
          disabled={!canRefine}
          onChange={(event) => {
            const value = Number(event.target.value)
            setInputRange({ from, to: value })
          }}
          onKeyUp={() => {
            applyRange()
          }}
          value={to}
        />
      </div>

      <ProductSearchRefinementClearRefinement
        className="hidden self-start desktop:inline-flex"
        includedAttributes={[attribute]}
        onResetFilter={(): void => {
          setInputRange({ from: min, to: max })
        }}
      />
    </div>
  )
}
